/* Example usage:
<marquee-component speed="100">
    <div class="marquee">
        <div class="marquee-content">
            <div class="marquee-item">Item 1</div>
            <div class="marquee-item">Item 2</div>
            <div class="marquee-item">Item 3</div>
            <div class="marquee-item">Item 4</div>
        </div>
    </div>
</marquee-component>
*/

if (!customElements.get('marquee-component')) {
  const DEBUG = false;
  const ANIMATION_DISTANCE_PCT = 50;
  const ANIMATION_PCT_FACTOR = 100 / ANIMATION_DISTANCE_PCT;
  const MARQUEE_START_TIME_KEY = 'marquee-start-time';

  // Get or create a shared start time for all marquees
  function getMarqueeStartTime() {
    let startTime = sessionStorage.getItem(MARQUEE_START_TIME_KEY);
    if (!startTime) {
      startTime = Date.now().toString();
      sessionStorage.setItem(MARQUEE_START_TIME_KEY, startTime);
    }
    return parseInt(startTime, 10);
  }

  class MarqueeComponent extends HTMLElement {
  constructor() {
    super();
    this.hash = window.crypto.randomUUID().slice(0, 7);
    this.startTime = getMarqueeStartTime();

    if (DEBUG) {
      this.addEventListener('animationiteration', (e) => {
        console.log(`animationiteration e`, e);
      });
    }
  }

  connectedCallback() {
    this.isInitializing = false;

    // Use requestAnimationFrame for faster initialization
    requestAnimationFrame(() => {
      this.initialise();

      // Debounce ResizeObserver
      let resizeTimeout;
      this.resizeObserver = new ResizeObserver(() => {
        if (this.isInitializing) return;
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => this.initialise(), 500);
      });
      this.resizeObserver.observe(this);
    });
  }

  disconnectedCallback() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  initialise() {
    if (this.isInitializing) return;
    this.isInitializing = true;

    try {
      const marquee = this.querySelector('.marquee');
      const marqueeContent = this.querySelector('.marquee-content');

      if (!marquee || !marqueeContent) {
        this.isInitializing = false;
        return;
      }

      const windowWidth = window.innerWidth;

      this.removeClonedContainers();
      this.addInitialStyles();

      const marqueeContentWidth = marqueeContent.scrollWidth;
      const numContainers = ANIMATION_PCT_FACTOR * Math.ceil(windowWidth / marqueeContentWidth);

      // Cap maximum clones to prevent excessive DOM nodes
      const maxClones = Math.min(numContainers - 1, 8);

      for (let i = 0; i < maxClones; i++) {
        const clone = marqueeContent.cloneNode(true);
        marquee.appendChild(clone);
      }

      this.setAnimationDuration();
    } finally {
      this.isInitializing = false;
    }
  }

  removeClonedContainers() {
    const marqueeContentEls = this.querySelectorAll('.marquee-content');
    marqueeContentEls?.forEach((el, index) => {
      if (index > 0) el.remove();
    });
  }

  setAnimationDuration() {
    if (!this.isConnected) return;
    const marquee = this.querySelector('.marquee');

    let speed = 0;
    try {
      speed = Number(this.getAttribute('speed')) ?? 0;
    } catch (e) {
      console.error('Error parsing speed attribute', e);
      speed = 0;
    }

    if (speed <= 0) {
      marquee.style['animation-play-state'] = 'paused';
      return;
    } else {
      marquee.style['animation-play-state'] = 'running';
    }

    const marqueeWidth = marquee.scrollWidth;
    const animationDistance = marqueeWidth / ANIMATION_PCT_FACTOR;
    const animationDuration = animationDistance / speed;

    if (DEBUG) {
      console.log({ marqueeWidth, animationDuration, animationDistance });
    }

    this.appendAnimationStyles(animationDuration, animationDistance, speed);
  }

  static get observedAttributes() {
    return ['speed'];
  }

  attributeChangedCallback(name, oldVal, newVal) {
    if (name === 'speed' && oldVal !== newVal) {
      this.addInitialStyles();
      this.setAnimationDuration();
    }
  }

  addInitialStyles() {
    const styleTags = this.querySelectorAll('style');
    if (styleTags) {
      styleTags.forEach((style) => style.remove());
    }
    const marqueeEl = this.querySelector('.marquee');
    marqueeEl.classList.add(`marquee-${this.hash}`);

    const style = document.createElement('style');
    style.textContent = /* css */ `
      marquee-component {
        display: block;
        width: 100%;
        overflow: hidden;
      }

      .marquee-${this.hash} {
        display: flex;
        width: max-content;
      }

      .marquee-${this.hash} .marquee-content {
        display: flex;
        width: max-content;
      }
    `;
    this.prepend(style);
  }

  appendAnimationStyles(animationDuration, animationDistance, speed) {
    const style = this.querySelector('style');
    const marquee = this.querySelector('.marquee');

    // Calculate elapsed time and current position
    const elapsedSeconds = (Date.now() - this.startTime) / 1000;
    const elapsedPixels = (elapsedSeconds * speed) % animationDistance;

    // Set initial position immediately via inline style
    marquee.style.transform = `translateX(-${elapsedPixels}px)`;

    // Force a reflow to ensure the initial position is rendered
    marquee.offsetHeight;

    // Now add the animation, starting from current position
    const animationStyles = /* css */ `
      .marquee-${this.hash} {
        animation: marquee-${this.hash}-animation ${animationDuration}s linear infinite;
      }

      @keyframes marquee-${this.hash}-animation {
        0% {
          transform: translateX(-${elapsedPixels}px);
        }
        100% {
          transform: translateX(-${elapsedPixels + animationDistance}px);
        }
      }
    `;
    style.textContent += animationStyles;

    // Add ready class to reveal marquee (CSS handles visibility)
    marquee.classList.add('marquee-ready');
  }
  }
  customElements.define('marquee-component', MarqueeComponent);
}
